import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/24/solid';
import { AdminProduct, ProductUpdateDto } from '../../types/product';
import adminProductService from '../../services/adminProductService';
import { ImageUploader } from '../../components/admin/ImageUploader';
import { MultipleImageUploader } from '../../components/admin/MultipleImageUploader';
import { CertificateUploader } from '../../components/admin/CertificateUploader';

interface ProductEditModalProps {
    product: AdminProduct;
    onClose: () => void;
    onUpdate: (updatedProduct: AdminProduct) => void;
}

/**
 * Модальное окно для редактирования товара
 */
const ProductEditModal: React.FC<ProductEditModalProps> = ({ product, onClose, onUpdate }) => {
    const [formData, setFormData] = useState<ProductUpdateDto>({
        name: product.name,
        description: product.description || '',
        price: product.price,
        category: product.category || '',
        minQuantity: product.minQuantity || 1,
        maxQuantity: product.maxQuantity || 999,
        availability: product.availability,
        active: product.active,
    });

    const [currentProduct, setCurrentProduct] = useState<AdminProduct>(product);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<'info' | 'images' | 'certificate'>('info');

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) => {
        const { name, value, type } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]:
                type === 'number'
                    ? value === ''
                        ? undefined
                        : parseFloat(value)
                    : type === 'checkbox'
                    ? (e.target as HTMLInputElement).checked
                    : value,
        }));
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            setError(null);
            const updatedProduct = await adminProductService.updateProduct(product.id, formData);
            setCurrentProduct(updatedProduct);
            onUpdate(updatedProduct);
        } catch (err: any) {
            setError(err.message || 'Ошибка при сохранении');
        } finally {
            setSaving(false);
        }
    };

    const handleMainImageUpload = async (file: File) => {
        const updatedProduct = await adminProductService.uploadMainImage(product.id, file);
        setCurrentProduct(updatedProduct);
    };

    const handleMainImageRemove = async () => {
        const updatedProduct = await adminProductService.removeMainImage(product.id);
        setCurrentProduct(updatedProduct);
    };

    const handleAdditionalImageAdd = async (file: File) => {
        const updatedProduct = await adminProductService.addAdditionalImage(product.id, file);
        setCurrentProduct(updatedProduct);
    };

    const handleAdditionalImageRemove = async (imageUrl: string) => {
        const updatedProduct = await adminProductService.removeAdditionalImage(
            product.id,
            imageUrl
        );
        setCurrentProduct(updatedProduct);
    };

    const handleCertificateUpload = async (file: File) => {
        const updatedProduct = await adminProductService.uploadCertificate(product.id, file);
        setCurrentProduct(updatedProduct);
        setFormData((prev) => ({
            ...prev,
            certificateName: updatedProduct.certificateName,
        }));
    };

    const handleCertificateRemove = async () => {
        const updatedProduct = await adminProductService.removeCertificate(product.id);
        setCurrentProduct(updatedProduct);
        setFormData((prev) => ({
            ...prev,
            certificateName: undefined,
        }));
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                {/* Заголовок */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">
                            Редактирование товара
                        </h2>
                        <p className="text-sm text-gray-500 mt-1">
                            {product.name} • GUID: {product.guid}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <XMarkIcon className="h-6 w-6" />
                    </button>
                </div>

                {/* Табы */}
                <div className="flex border-b border-gray-200 px-6">
                    <button
                        onClick={() => setActiveTab('info')}
                        className={`py-3 px-4 font-medium text-sm border-b-2 transition-colors ${
                            activeTab === 'info'
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        Основная информация
                    </button>
                    <button
                        onClick={() => setActiveTab('images')}
                        className={`py-3 px-4 font-medium text-sm border-b-2 transition-colors ${
                            activeTab === 'images'
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        Изображения
                        {(currentProduct.mainImageUrl ||
                            (currentProduct.additionalImages &&
                                currentProduct.additionalImages.length > 0)) && (
                            <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                                {(currentProduct.mainImageUrl ? 1 : 0) +
                                    (currentProduct.additionalImages?.length || 0)}
                            </span>
                        )}
                    </button>
                    <button
                        onClick={() => setActiveTab('certificate')}
                        className={`py-3 px-4 font-medium text-sm border-b-2 transition-colors ${
                            activeTab === 'certificate'
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        Сертификат
                        {currentProduct.certificateUrl && (
                            <span className="ml-2 text-xs bg-purple-100 text-purple-800 px-2 py-0.5 rounded-full">
                                1
                            </span>
                        )}
                    </button>
                </div>

                {/* Содержимое */}
                <div className="flex-1 overflow-y-auto p-6">
                    {error && (
                        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
                            {error}
                        </div>
                    )}

                    {/* Вкладка "Основная информация" */}
                    {activeTab === 'info' && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Название товара *
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Категория
                                    </label>
                                    <input
                                        type="text"
                                        name="category"
                                        value={formData.category}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Описание
                                </label>
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    rows={4}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Цена (₸)
                                    </label>
                                    <input
                                        type="number"
                                        name="price"
                                        value={formData.price || ''}
                                        onChange={handleInputChange}
                                        min="0"
                                        step="0.01"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Мин. количество
                                    </label>
                                    <input
                                        type="number"
                                        name="minQuantity"
                                        value={formData.minQuantity}
                                        onChange={handleInputChange}
                                        min="1"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Макс. количество
                                    </label>
                                    <input
                                        type="number"
                                        name="maxQuantity"
                                        value={formData.maxQuantity}
                                        onChange={handleInputChange}
                                        min="1"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Доступность
                                    </label>
                                    <select
                                        name="availability"
                                        value={formData.availability}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="available">В наличии</option>
                                        <option value="limited">Ограничено</option>
                                        <option value="out_of_stock">Нет в наличии</option>
                                    </select>
                                </div>

                                <div className="flex items-center pt-7">
                                    <label className="flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            name="active"
                                            checked={formData.active}
                                            onChange={handleInputChange}
                                            className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="ml-2 text-sm font-medium text-gray-700">
                                            Активный товар
                                        </span>
                                    </label>
                                </div>
                            </div>

                            {/* Информация из 1С (только для чтения) */}
                            <div className="mt-6 pt-6 border-t border-gray-200">
                                <h3 className="text-sm font-medium text-gray-700 mb-3">
                                    Данные из 1С (только для чтения)
                                </h3>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                    <div>
                                        <span className="text-gray-500">Код:</span>
                                        <p className="font-medium">{product.cod || '—'}</p>
                                    </div>
                                    <div>
                                        <span className="text-gray-500">Артикул:</span>
                                        <p className="font-medium">{product.article || '—'}</p>
                                    </div>
                                    <div>
                                        <span className="text-gray-500">Ед. измерения:</span>
                                        <p className="font-medium">{product.measure || '—'}</p>
                                    </div>
                                    <div>
                                        <span className="text-gray-500">GUID:</span>
                                        <p className="font-medium text-xs">{product.guid}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Вкладка "Изображения" */}
                    {activeTab === 'images' && (
                        <div>
                            <ImageUploader
                                imageUrl={currentProduct.mainImageUrl}
                                onUpload={handleMainImageUpload}
                                onRemove={
                                    currentProduct.mainImageUrl
                                        ? handleMainImageRemove
                                        : undefined
                                }
                                label="Основное изображение"
                                helpText="Это изображение будет отображаться в карточке товара"
                            />

                            <div className="mt-8 pt-8 border-t border-gray-200">
                                <MultipleImageUploader
                                    images={currentProduct.additionalImages || []}
                                    onAdd={handleAdditionalImageAdd}
                                    onRemove={handleAdditionalImageRemove}
                                    maxImages={5}
                                />
                            </div>
                        </div>
                    )}

                    {/* Вкладка "Сертификат" */}
                    {activeTab === 'certificate' && (
                        <div>
                            <CertificateUploader
                                certificateUrl={currentProduct.certificateUrl}
                                certificateName={currentProduct.certificateName}
                                onUpload={handleCertificateUpload}
                                onRemove={
                                    currentProduct.certificateUrl
                                        ? handleCertificateRemove
                                        : undefined
                                }
                            />
                        </div>
                    )}
                </div>

                {/* Футер с кнопками */}
                <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
                    <button
                        onClick={onClose}
                        className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                        Закрыть
                    </button>
                    
                    {activeTab === 'info' && (
                        <button
                            onClick={handleSave}
                            disabled={saving}
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                        >
                            {saving ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                                    Сохранение...
                                </>
                            ) : (
                                <>
                                    <CheckIcon className="h-4 w-4 mr-2" />
                                    Сохранить изменения
                                </>
                            )}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProductEditModal;

